import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {Withdrawal} from '../../BuilderWithdrawal/BuilderWithdrawal';
import {Transfer} from '../../BuilderTransfer/BuilderTransfer';

import {BuilderSetUpNew} from '../../BuilderSetUpNew/BuilderSetUpNew';
import {BuilderProtectionPlan} from '../../BuilderSetUpNew/BuilderProtectionPlan';
import {BuilderAnnuityPlan} from '../../BuilderSetUpNew/BuilderAnnuityPlan';
import {POTS_ANNUITY, POTS_PROTECTION} from '../../../../utils/potsFilter';
import {Contribute} from '../../BuilderContribute/BuilderContribute';
import {useAccountContext} from '../../../../contexts/AccountContext';
import {OVRecommendActionEnum} from '../../../../schema-types/globalTypes';
import {builderFieldsType} from '../builderFields';
import {PlanLabelType, BuilderProps} from './builderValidators';
import {BuilderAction} from '../../BuilderAction/BuilderAction';
import {BuilderModify} from '../../BuilderModify/BuilderModify';
import {setContactnameFromGoals} from './builderUtils';
import {useBuilderContext} from '../useBuilderContext';
import {BuilderParserStep} from '../../BuilderParserStep/BuilderParserStep';
import {useAutoForm} from '../../../../components/AutoForm/useAutoForm';
import {BuilderAdHocFee} from '../../BuilderAdHocFee/BuilderAdHocFee';

export const PLAN_LABELS: Record<string, PlanLabelType> = {
  withdraw: {
    first: 'From',
    second: 'Send funds to',
  },
  contribute: {
    first: 'Take from',
    second: 'Give to',
  },
  transfer: {
    first: 'From',
    second: 'To',
  },
  setUpNew: {
    first: 'Open',
  },
  modify: {
    first: 'Modifying',
  },
  close: {
    first: 'Closing',
    second: 'Send remaining funds to',
  },
  adHocFee: {
    first: 'Charge Ad-hoc Fee to',
  },
};

export const BuilderPlanTypeContainer: React.FC<{
  selectionError: boolean;
}> = ({selectionError}) => {
  const {values} = useAutoForm<builderFieldsType>();
  const [contactName, setContactName] = useState<string>('');
  const {account} = useAccountContext();
  const {builderDetails, isSPPDA} = useBuilderContext();

  useEffect(() => {
    if (account) {
      setContactnameFromGoals({
        values,
        account,
        setContactName,
      });
    }
  }, [account, values.contact]);

  if (!values.planAction) {
    return <BuilderParserStep errorText="Please complete the above" />;
  }

  if (selectionError) {
    return (
      <Box
        justifyContent="center"
        alignContent="center"
        width="100%"
        height={300}
        display="flex"
      >
        <Typography variant="h3">
          Sorry you cannot select this combination of options together.
        </Typography>
      </Box>
    );
  }

  const sourceDestinationLabels: PlanLabelType =
    values.planAction && values.planAction in PLAN_LABELS
      ? {...PLAN_LABELS[values.planAction]}
      : {};

  const props: BuilderProps = {
    contactName,
    sourceDestinationLabels,
  };

  const error = (builderDetails && builderDetails.action === undefined) || false;

  if (error) {
    <BuilderParserStep
      errorText={`Sorry, something went wrong when previously saving this ${
        isSPPDA ? 'instruction' : 'recommendation'
      }`}
    />;
  }

  if (values.planAction === OVRecommendActionEnum.SET_UP_NEW) {
    if (values.mainPlanType === POTS_PROTECTION) {
      return <BuilderProtectionPlan {...props} />;
    }

    if (values.mainPlanType === POTS_ANNUITY) {
      return <BuilderAnnuityPlan {...props} />;
    }

    return <BuilderSetUpNew {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.TRANSFER) {
    return <Transfer {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.CONTRIBUTE) {
    return <Contribute {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.WITHDRAW) {
    return <Withdrawal {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.MODIFY) {
    return <BuilderModify {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.ACTION) {
    return <BuilderAction {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.AD_HOC_FEE) {
    return <BuilderAdHocFee {...props} />;
  }

  return null;
};
